import { LocalDate } from '@js-joda/core';
import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { DateRange, formatMessageBundle, InvalidValueType } from '@unifocus/js-utils';
import {
   LocalDatePresetRange,
   LocalDateRangePicker
} from '../../../controls/pickers/localdaterangepicker';
import { Button } from '../../../controls/button';
import messages from '../../../../localized-messages';
import { Icons } from '@unifocus/react-icons';
import { Popover } from '../../../display/popover';
import { DateRangeInputValueType } from './DateRangeInput';
import { useWindowSize } from '../../../../hooks/WindowSizeHook';
import { Drawer } from '../../../controls/drawer';
import { VerticalPanel } from '../../../layout/verticalpanel';

export interface DateRangePickerTriggerProps {
   value?: DateRangeInputValueType;
   disabled?: boolean;
   readOnly?: boolean;
   onChange?: (value?: DateRangeInputValueType) => void;
   minDate?: LocalDate;
   maxDate?: LocalDate;
   formatCell?: (date: LocalDate) => ReactElement | null;
   disableCell?: (date: LocalDate) => boolean;
   referenceDateRange?: DateRange;
   presetRanges?: LocalDatePresetRange[];
   fullScreen?: boolean;
}

const localDateRangePickerFullWidth = 582;
const localDateRangePickerStackWidth = localDateRangePickerFullWidth / 2;

export const DateRangePickerTrigger = (props: DateRangePickerTriggerProps) => {
   const [dateRangePickerVisible, setDateRangePickerVisible] = useState(false);

   const [count, setCount] = useState(0);

   const { onChange: propsOnChange, minDate, maxDate, value, presetRanges, referenceDateRange, formatCell, disableCell, disabled, readOnly, fullScreen } = props;

   const [currentDateRange, setCurrentDateRange] = React.useState<DateRange | undefined>(value !== undefined && value !== InvalidValueType.Invalid && value instanceof DateRange ? value : undefined);

   const initialDateRangeRef = useRef<DateRange | undefined>(currentDateRange);

   const [disableOkButton, setDisableOkButton] = useState<boolean>(true);

   const { width: windowWidth } = useWindowSize();
   const width = (windowWidth && windowWidth >= localDateRangePickerFullWidth) ? localDateRangePickerFullWidth : localDateRangePickerStackWidth;

   useEffect(()=>{
      const dateRange = value !== undefined && value !== InvalidValueType.Invalid && value instanceof DateRange ? value : undefined;
      setCurrentDateRange(dateRange);
      initialDateRangeRef.current = dateRange;
      setDisableOkButton(true);
   }, [value]);

   const onOk = useCallback((value: DateRange | undefined) => {
      if (propsOnChange) {
         propsOnChange(value);
      }
      setDateRangePickerVisible(false);
      setDisableOkButton(true);
      initialDateRangeRef.current = value;
      setCount((count) => count+1);
   },[propsOnChange]);

   const onCancel = useCallback(() => {
      setDateRangePickerVisible(false);
      setDisableOkButton(true);
      setCurrentDateRange(initialDateRangeRef.current);
      setCount((count) => count+1);
   },[]);

   const onVisibleChange = useCallback((visible: boolean) => {
      setDateRangePickerVisible(visible);
      if (!visible) {
         setCurrentDateRange(initialDateRangeRef.current);
         setCount((count) => count+1);
      }
   },[]);

   const okHandler = useCallback(() => onOk(currentDateRange),
                                 [currentDateRange, onOk]);

   const divStyle = useMemo(()=> {
      return {
         width: fullScreen ? '100%' : width
      };
   },[width, fullScreen ]);

   const onKeyUpHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
      if (e.keyCode === 13) {
         okHandler();
      }
   },[okHandler]);

   const actionItems = useMemo(()=>(
      <LocalDateRangePickerActionItems disableOkButton={disableOkButton} onOk={okHandler} onCancel={onCancel} />
   ),[disableOkButton, okHandler, onCancel]);

   const localDateRangePickerOnChange = useCallback((dateRange: DateRange) => {
      setCurrentDateRange(dateRange);
      setDisableOkButton(false);
   },[]);

   const datePicker = useMemo(()=> {
      return (<div style={divStyle} onKeyUp={onKeyUpHandler}>
         <LocalDateRangePicker
            key={count}
            dateRange={currentDateRange}
            onChange={localDateRangePickerOnChange}
            presetRanges={presetRanges}
            referenceDateRange={referenceDateRange}
            formatCell={formatCell}
            stacked={fullScreen || width === localDateRangePickerStackWidth}
            disableCell={disableCell}
            actionItems={fullScreen ? undefined : actionItems}
            minDate={minDate}
            maxDate={maxDate}
         />
      </div>);
   },[count, localDateRangePickerOnChange, currentDateRange, minDate, maxDate, disableCell, formatCell, presetRanges, referenceDateRange, width, actionItems, divStyle, onKeyUpHandler, fullScreen]);

   return (
      <>
        {(disabled || readOnly) ? <Icons.Calendar/> :
         (fullScreen ?
                  <>
                    <Icons.Calendar onClick={()=>setDateRangePickerVisible(true)}/>
                    <Drawer
                       position={'bottom'}
                       closable={false}
                       visible={dateRangePickerVisible}
                       afterVisibleChange={onVisibleChange}
                       onClose={onCancel}
                       footer={actionItems}
                       bodyStyle={{
                          padding: 0
                       }}
                    >
                       <VerticalPanel align={'bottom'}>
                          {datePicker}
                       </VerticalPanel>
                    </Drawer>
                 </>
            :
               <Popover
                  overlayClassName={'uf-date-range-input-picker-popover'}
                  visible={dateRangePickerVisible}
                  onVisibleChange={onVisibleChange}
                  trigger={'click'}
                  content={datePicker}
               >
                  <Icons.Calendar/>
               </Popover>
         )
      }
      </>
   );
};

interface LocalDateRangePickerActionItemsProps {
   disableOkButton?: boolean;
   onOk?: () => void;
   onCancel?: () => void;
}

const LocalDateRangePickerActionItems = (props: LocalDateRangePickerActionItemsProps) => {
   return (
      <>
         <Button
            size={'small'}
            onClick={props.onCancel}
         >
            {formatMessageBundle(messages, 'res_cancel')}
         </Button>
         <Button
            size={'small'}
            type={'primary'}
            onClick={props.onOk}
            disabled={props.disableOkButton}
         >
            {formatMessageBundle(messages, 'res_ok')}
         </Button>
      </>
   );
};
